import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CameraFrontIcon from '@mui/icons-material/CameraFront';
import TaskIcon from '@mui/icons-material/Task';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CircularProgress from '@mui/material/CircularProgress';
import { useAppDispatch } from '../../../../redux/hooks';
import { getPrivateFile } from '../../../../redux/modules/media/media.actions';
import { mediaCacheSelector } from '../../../../redux/modules/media/media.selectors';
import { fetchUserAttachments } from '../../../../redux/modules/user/user.actions';
import {
  userAttachmentsLoadingSelector,
  userAttachmentsSelector,
  userSelector,
} from '../../../../redux/modules/user/user.selectors';
import { FetchMediaFileResponse } from '../../../../types/media';
import { DocumentTypes } from '../../../../types/user';
import COLORS from '../../../../constants/colors';
import { isDispatchApiSuccess } from '../../../../utils/api';
import { AddAttachmentDialog } from './AddAttachmentDialog';
import { CARD_BORDER, SectionCard } from '../../../../components/Section';

interface AttachmentsTabProps {
  id: number;
}

const mediaCacheTTL = 5 * 60 * 1000; // 5 mins

export function AttachmentsTab({ id }: AttachmentsTabProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = React.useState(false);
  const { error } = useSelector(userSelector);
  const attachments = useSelector(userAttachmentsSelector);
  const mediaCache = useSelector(mediaCacheSelector);
  const isLoading = useSelector(userAttachmentsLoadingSelector);

  const openInNewTab = async (url: string) => {
    if (mediaCache[url]?.url && mediaCache[url].timestamp + mediaCacheTTL > Date.now()) {
      return window.open(mediaCache[url].url, '_blank', 'noopener,noreferrer');
    }

    const result = await dispatch(getPrivateFile(url));

    if (isDispatchApiSuccess<FetchMediaFileResponse>(result)) {
      window.open(result.payload.data.url, '_blank', 'noopener,noreferrer');
    } else {
      enqueueSnackbar(`Error: ${result?.error?.message}`, { variant: 'error' });
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchUserAttachments(id));
    }
  }, []);

  const onAddNew = (): void => {
    setOpen(true);
  };

  const handleClose = (isReload: boolean) => {
    if (isReload) {
      dispatch(fetchUserAttachments(id));
    }
    setOpen(false);
  };

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: '16px', borderRadius: '8px' }}>
          {error.message}
        </Alert>
      )}

      {/* Max Number of Files to be uploaded is 5 */}
      {attachments.length < 5 && <AddAttachmentDialog open={open} userId={id} onClose={handleClose} />}

      <SectionCard
        title="Attachments"
        description={`${attachments.length} of 5 documents uploaded`}
        action={
          attachments.length < 5 ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={onAddNew}
              aria-label="Create Action"
              sx={{ textTransform: 'none' }}
            >
              Add attachment
            </Button>
          ) : undefined
        }
      >
        {isLoading && <CircularProgress size={24} />}

        {!isLoading && attachments.length === 0 && (
          <Typography sx={{ fontSize: '0.875rem', color: COLORS.LIGHT_GRAY }}>No documents uploaded yet.</Typography>
        )}

        <Box sx={{ display: 'grid', gap: '12px' }}>
          {attachments.map((at) => (
            <Box
              key={`attachment-item-${at.id}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                p: '12px 16px',
                borderRadius: '10px',
                border: `1px solid ${CARD_BORDER}`,
              }}
            >
              <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(COLORS.PRIMARY_BLUE, 0.1), color: '#1E72A8' }}>
                {at.documentType.type === DocumentTypes.OTHER && <InsertDriveFileIcon sx={{ fontSize: '18px' }} />}
                {at.documentType.type === DocumentTypes.SELFIE && <CameraFrontIcon sx={{ fontSize: '18px' }} />}
                {at.documentType.type === DocumentTypes.VERIFICATION_DOCUMENT && <TaskIcon sx={{ fontSize: '18px' }} />}
              </Avatar>

              <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: COLORS.MAIN_DARK }}>
                  {at.documentType.type}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: COLORS.LIGHT_GRAY,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={at.documentLink}
                >
                  {at.documentLink}
                </Typography>
              </Box>

              <Button
                size="small"
                variant="outlined"
                color="primary"
                endIcon={<OpenInNewIcon />}
                onClick={() => openInNewTab(at.documentLink)}
                sx={{ flexShrink: 0, textTransform: 'none' }}
              >
                Open
              </Button>
            </Box>
          ))}
        </Box>
      </SectionCard>
    </>
  );
}
